import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Search, Filter, X, Loader2, Wrench } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import CustomPartModal from "@/components/CustomPartModal";
import axios from "axios";

const API = "/api";

const categoryLabels = {
  all: "All Products",
  maxim: "Maxim (MG-08)",
  pkm: "PKM",
  "mp35-bergmann": "MP-35 Bergmann",
  ak: "AK",
  merch: "Merchandise",
};

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState(["all"]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showCustomPartModal, setShowCustomPartModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/categories`),
        ]);
        setProducts(productsRes.data);
        setFilteredProducts(productsRes.data);
        setCategories(categoriesRes.data.categories);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = [...products];

    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.description.toLowerCase().includes(query)
      );
    }

    // Sort
    const priceOf = (p) => (p.sale_price && p.sale_price > 0 ? p.sale_price : (p.price || 0));
    if (sortBy === "price-asc") {
      filtered.sort((a, b) => priceOf(a) - priceOf(b));
    } else if (sortBy === "price-desc") {
      filtered.sort((a, b) => priceOf(b) - priceOf(a));
    } else if (sortBy === "name-asc") {
      filtered.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (sortBy === "name-desc") {
      filtered.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    } else if (sortBy === "newest") {
      filtered.sort((a, b) => {
        const at = new Date(a.created_at || 0).getTime();
        const bt = new Date(b.created_at || 0).getTime();
        return bt - at;
      });
    }

    setFilteredProducts(filtered);
  }, [selectedCategory, searchQuery, sortBy, products]);

  const clearFilters = () => {
    setSelectedCategory("all");
    setSearchQuery("");
    setSortBy("newest");
  };

  const hasActiveFilters = selectedCategory !== "all" || searchQuery.trim();

  return (
    <div className="min-h-screen pt-24" data-testid="shop-page">
      <Seo
        title="Shop All Parts"
        description="Browse the full catalog of reproduction parts for vintage military firearms — Arisaka, Maxim, MP-35 Bergmann, PKM, AK, and more."
        path="/shop"
      />
      {/* Hero Banner */}
      <section className="py-16 bg-[#0f0f0f] border-b border-white/5">
        <div className="container-tactical">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <p className="font-mono text-[#C0A062] text-sm tracking-[0.3em] uppercase mb-4">
              Browse Our Catalog
            </p>
            <h1 className="font-heading font-black text-5xl md:text-6xl text-white uppercase tracking-tighter mb-4">
              Shop Parts
            </h1>
            <p className="text-[#A1A1AA] text-lg">
              High-quality reproduction parts for vintage military firearms.
              From WWI Maxim machine guns to Cold War-era PKMs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters & Products */}
      <section className="section-spacing bg-[#0a0a0a]">
        <div className="container-tactical">
          {/* Filter Bar */}
          <div className="flex flex-col lg:flex-row gap-6 mb-12">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717a]" />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-tactical pl-12 w-full"
                data-testid="search-input"
              />
            </div>

            {/* Mobile Filter Toggle */}
            <Button
              variant="outline"
              className="lg:hidden border-white/10 bg-transparent hover:bg-white/5 text-white"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="filter-toggle"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <span className="ml-2 w-5 h-5 rounded-full bg-[#C0A062] text-black text-xs flex items-center justify-center">
                  !
                </span>
              )}
            </Button>

            {/* Desktop Categories */}
            <div className="hidden lg:flex items-center gap-2 flex-wrap">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`category-badge cursor-pointer transition-all hover:border-[#C0A062]/50 ${
                    selectedCategory === category ? "active" : ""
                  }`}
                  data-testid={`category-${category}`}
                >
                  {categoryLabels[category] || category}
                </button>
              ))}
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-[#A1A1AA] hover:text-white"
                data-testid="clear-filters"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Mobile Filters Panel */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden mb-8 p-6 bg-[#121212] border border-white/5 rounded-sm"
            >
              <p className="font-mono text-[#C0A062] text-xs uppercase tracking-wider mb-4">
                Categories
              </p>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => {
                      setSelectedCategory(category);
                      setShowFilters(false);
                    }}
                    className={`category-badge cursor-pointer ${
                      selectedCategory === category ? "active" : ""
                    }`}
                    data-testid={`mobile-category-${category}`}
                  >
                    {categoryLabels[category] || category}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Results Count + Sort */}
          <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
            <p className="text-[#A1A1AA]">
              Showing{" "}
              <span className="text-white font-semibold">
                {filteredProducts.length}
              </span>{" "}
              {filteredProducts.length === 1 ? "product" : "products"}
              {selectedCategory !== "all" && (
                <span>
                  {" "}
                  in{" "}
                  <span className="text-[#C0A062]">
                    {categoryLabels[selectedCategory] || selectedCategory}
                  </span>
                </span>
              )}
            </p>
            <div className="flex items-center gap-2">
              <label htmlFor="shop-sort" className="text-[10px] font-mono text-[#71717a] uppercase tracking-wider">Sort</label>
              <select
                id="shop-sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-[#121212] border border-white/10 text-[#D4D4D8] text-sm rounded-sm px-3 py-1.5 focus:outline-none focus:border-[#C0A062]/60"
                data-testid="shop-sort-select"
              >
                <option value="newest">Newest first</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="name-asc">Name: A → Z</option>
                <option value="name-desc">Name: Z → A</option>
              </select>
            </div>
          </div>

          {/* Products Grid */}
          {isLoading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 text-[#C0A062] animate-spin" />
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product, i) => (
                <ProductCard key={product.id} product={product} index={i} />
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-24"
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#121212] border border-white/5 flex items-center justify-center">
                <Search className="w-6 h-6 text-[#71717a]" />
              </div>
              <h3 className="font-heading font-bold text-white text-xl uppercase mb-2">
                No Products Found
              </h3>
              <p className="text-[#A1A1AA] mb-6">
                Try adjusting your search or filter criteria.
              </p>
              <Button
                onClick={clearFilters}
                className="btn-secondary"
                data-testid="reset-filters"
              >
                Reset Filters
              </Button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Custom Order CTA */}
      <section className="py-16 bg-[#121212] border-t border-white/5">
        <div className="container-tactical">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 p-8 bg-[#0a0a0a] border border-white/5 rounded-sm">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-sm bg-[#C0A062]/10 flex items-center justify-center">
                <Wrench className="w-7 h-7 text-[#C0A062]" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-xl md:text-2xl text-white uppercase tracking-tight">
                  Don't See What You Need?
                </h2>
                <p className="text-[#A1A1AA] text-sm md:text-base">
                  We specialize in custom reproductions. Request a quote for your specific part.
                </p>
              </div>
            </div>
            <Button
              className="btn-primary whitespace-nowrap"
              onClick={() => setShowCustomPartModal(true)}
              data-testid="custom-part-cta"
            >
              <Wrench className="w-4 h-4 mr-2" />
              Request Custom Part
            </Button>
          </div>
        </div>
      </section>

      {/* Custom Part Modal */}
      <CustomPartModal
        isOpen={showCustomPartModal}
        onClose={() => setShowCustomPartModal(false)}
      />
    </div>
  );
}
